import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, useMemo, useEffect, lazy, Suspense } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableHead, TableHeader, TableRow, TableBody, TableCell } from "@/components/ui/table";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import {
  adminMe, adminListEnquiries, adminListBookings, adminUpdateBookingStatus,
  adminListEvents, adminCreateEvent, adminDeleteEvent,
  adminListGallery, adminCreateGallery, adminDeleteGallery,
  adminGetGalleryPageSize, adminSetGalleryPageSize, adminGetSiteLogo, adminSetSiteLogo,
  adminListAds, adminCreateAd, adminDeleteAd, adminToggleAd,
  adminListFbPosts, adminCreateFbPost, adminDeleteFbPost,
  adminListUsers,
  adminListAdmins, adminInviteAdmin, adminRemoveAdmin, adminUpdateAdminPermissions, adminRevokeInvitation,
  PERMISSION_KEYS, type PermissionKey,
} from "@/lib/api/admin.functions";
import { Switch } from "@/components/ui/switch";
import { SiteHeader } from "@/components/SiteHeader";
import { Trash2, Mail } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";

const AnalyticsTab = lazy(() => import("@/components/admin/AnalyticsTab"));
const SeoTab = lazy(() => import("@/components/admin/SeoTab"));
const AdminNotifications = lazy(() => import("@/components/admin/AdminNotifications"));
const ThumbnailCropper = lazy(() =>
  import("@/components/ThumbnailCropper").then((m) => ({ default: m.ThumbnailCropper })),
);
const FacebookFeed = lazy(() =>
  import("@/components/FacebookFeed").then((m) => ({ default: m.FacebookFeed })),
);


async function uploadToGallery(file: File): Promise<string> {
  const ext = file.name.split(".").pop() ?? "jpg";
  const path = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
  const { error } = await supabase.storage.from("gallery").upload(path, file, { cacheControl: "3600", upsert: false });
  if (error) throw new Error(error.message);
  const { data } = supabase.storage.from("gallery").getPublicUrl(path);
  return data.publicUrl;
}

export const Route = createFileRoute("/_authenticated/admin")({
  head: () => ({ meta: [{ title: "Admin · Surer Adda" }, { name: "robots", content: "noindex" }] }),
  component: AdminPanel,
});

function AdminPanel() {
  const me = useServerFn(adminMe);
  const { data: roleData, isLoading } = useQuery({ queryKey: ["adminMe"], queryFn: () => me() });

  if (isLoading) return <Shell><p className="p-8">Loading…</p></Shell>;
  if (!roleData?.isAdmin) return (
    <Shell>
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="text-2xl font-semibold">Access Denied</h1>
        <p className="mt-2 text-muted-foreground">You don't have admin privileges.</p>
      </div>
    </Shell>
  );

  const perms = roleData.permissions ?? ["*"];
  const has = (k: string) => perms.includes("*") || perms.includes(k);

  const allTabs: Array<{ key: string; label: string; node: React.ReactNode }> = [
    { key: "analytics", label: "Analytics", node: <Suspense fallback={<p className="p-4">Loading…</p>}><AnalyticsTab /></Suspense> },
    { key: "users", label: "Users", node: <UsersTab /> },
    { key: "enquiries", label: "Enquiries", node: <EnquiriesTab /> },
    { key: "bookings", label: "Bookings", node: <BookingsTab /> },
    { key: "events", label: "Events", node: <EventsTab /> },
    { key: "gallery", label: "Gallery", node: <GalleryTab /> },
    { key: "ads", label: "Advertisements", node: <AdsTab /> },
    { key: "fb", label: "Facebook Posts", node: <FbPostsTab /> },
    { key: "seo", label: "SEO", node: <Suspense fallback={<p className="p-4">Loading…</p>}><SeoTab /></Suspense> },
    { key: "admins", label: "Admin Users", node: <AdminsTab /> },
  ];
  const tabs = allTabs.filter((t) => has(t.key));

  if (tabs.length === 0) return (
    <Shell>
      <div className="mx-auto max-w-2xl p-8 text-center">
        <h1 className="text-2xl font-semibold">No tabs assigned</h1>
        <p className="mt-2 text-muted-foreground">Ask another admin to grant you access.</p>
      </div>
    </Shell>
  );

  return (
    <Shell>
      <Suspense fallback={null}><AdminNotifications /></Suspense>
      <div className="mx-auto max-w-7xl px-4 py-8">
        <h1 className="font-serif text-3xl text-primary">Admin Panel</h1>
        <p className="text-sm text-muted-foreground">
          {perms.includes("*") ? "Full access" : `Limited access: ${perms.join(", ")}`}
        </p>

        <Tabs defaultValue={tabs[0].key} className="mt-6">
          <TabsList className="flex h-auto flex-wrap gap-1">
            {tabs.map((t) => <TabsTrigger key={t.key} value={t.key}>{t.label}</TabsTrigger>)}
          </TabsList>
          {tabs.map((t) => <TabsContent key={t.key} value={t.key}>{t.node}</TabsContent>)}
        </Tabs>
      </div>
    </Shell>
  );
}

function Shell({ children }: { children: React.ReactNode }) {
  return <div className="min-h-screen bg-background"><SiteHeader />{children}</div>;
}

function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <Card>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className="mt-1 text-2xl font-semibold">{value}</p>
        {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
      </CardContent>
    </Card>
  );
}


function UsersTab() {
  const fn = useServerFn(adminListUsers);
  const { data, isLoading } = useQuery({ queryKey: ["adminUsers"], queryFn: () => fn() });
  const [search, setSearch] = useState("");
  const [provider, setProvider] = useState<"all" | "google" | "email" | "other">("all");
  const [role, setRole] = useState<"all" | "admin" | "user">("all");
  const [confirmed, setConfirmed] = useState<"all" | "yes" | "no">("all");

  const stats = data?.stats;
  const users = data?.users ?? [];
  const q = search.trim().toLowerCase();
  const filtered = useMemo(() => users.filter((u: any) => {
    if (q) {
      const hay = [u.email, u.full_name, u.profile_phone, u.phone, u.id].filter(Boolean).join(" ").toLowerCase();
      if (!hay.includes(q)) return false;
    }
    if (provider !== "all") {
      const providers: string[] = u.providers ?? [];
      if (provider === "other") {
        if (providers.some((p) => p === "google" || p === "email")) return false;
      } else if (!providers.includes(provider)) return false;
    }
    if (role !== "all") {
      const roles: string[] = u.roles ?? [];
      if (role === "admin" && !roles.includes("admin")) return false;
      if (role === "user" && roles.includes("admin")) return false;
    }
    if (confirmed === "yes" && !u.confirmed) return false;
    if (confirmed === "no" && u.confirmed) return false;
    return true;
  }), [users, q, provider, role, confirmed]);

  if (isLoading) return <p className="p-4">Loading…</p>;

  const resetFilters = () => { setSearch(""); setProvider("all"); setRole("all"); setConfirmed("all"); };
  const hasFilters = !!q || provider !== "all" || role !== "all" || confirmed !== "all";

  const exportCsv = () => {
    const headers = ["Signed Up", "Name", "Email", "Phone", "Providers", "Roles", "Confirmed", "Last Sign-in", "User ID"];
    const esc = (v: any) => {
      const s = v == null ? "" : String(v);
      return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
    };
    const rows = filtered.map((u: any) => [
      u.created_at ? new Date(u.created_at).toISOString() : "",
      u.full_name ?? "",
      u.email ?? "",
      u.profile_phone ?? u.phone ?? "",
      (u.providers ?? []).join("; "),
      (u.roles ?? []).join("; ") || "user",
      u.confirmed ? "Yes" : "No",
      u.last_sign_in_at ? new Date(u.last_sign_in_at).toISOString() : "",
      u.id,
    ].map(esc).join(","));
    const csv = "\uFEFF" + [headers.join(","), ...rows].join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `users-${new Date().toISOString().slice(0, 10)}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mt-4 space-y-4">
      {stats && (
        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <StatCard label="Total Signups" value={stats.total} />
          <StatCard label="Confirmed" value={stats.confirmed} />
          <StatCard label="Admins" value={(stats as any).admins ?? 0} />
          <StatCard label="Active (7d)" value={stats.activeLast7} hint="Signed in last 7 days" />
          <StatCard label="Active (30d)" value={stats.activeLast30} hint="Signed in last 30 days" />
          <StatCard label="New Today" value={stats.signupsToday} />
          <StatCard label="New (7d)" value={stats.signupsLast7} />
          <StatCard label="New (30d)" value={stats.signupsLast30} />
        </div>
      )}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between gap-2">
          <CardTitle>Users ({filtered.length}{hasFilters ? ` of ${users.length}` : ""})</CardTitle>
          <Button size="sm" variant="outline" onClick={exportCsv} disabled={filtered.length === 0}>
            Export CSV
          </Button>
        </CardHeader>
        <CardContent className="space-y-3 overflow-x-auto">
          <div className="grid gap-2 md:grid-cols-[1fr_auto_auto_auto_auto]">
            <Input
              placeholder="Search name, email, phone, ID…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            <Select value={provider} onValueChange={(v) => setProvider(v as any)}>
              <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Provider" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All providers</SelectItem>
                <SelectItem value="google">Google</SelectItem>
                <SelectItem value="email">Email</SelectItem>
                <SelectItem value="other">Other</SelectItem>
              </SelectContent>
            </Select>
            <Select value={role} onValueChange={(v) => setRole(v as any)}>
              <SelectTrigger className="w-full md:w-36"><SelectValue placeholder="Role" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All roles</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
                <SelectItem value="user">User</SelectItem>
              </SelectContent>
            </Select>
            <Select value={confirmed} onValueChange={(v) => setConfirmed(v as any)}>
              <SelectTrigger className="w-full md:w-40"><SelectValue placeholder="Confirmed" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All statuses</SelectItem>
                <SelectItem value="yes">Confirmed</SelectItem>
                <SelectItem value="no">Unconfirmed</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={resetFilters} disabled={!hasFilters}>Reset</Button>
          </div>
          <Table>
            <TableHeader><TableRow>
              <TableHead>Signed Up</TableHead><TableHead>Name</TableHead><TableHead>Email</TableHead>
              <TableHead>Phone</TableHead><TableHead>Provider</TableHead><TableHead>Role</TableHead>
              <TableHead>Confirmed</TableHead><TableHead>Last Sign-in</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {filtered.map((u: any) => (
                <TableRow key={u.id}>
                  <TableCell className="whitespace-nowrap text-xs">{new Date(u.created_at).toLocaleDateString()}</TableCell>
                  <TableCell>{u.full_name ?? "—"}</TableCell>
                  <TableCell className="text-xs">{u.email ?? "—"}</TableCell>
                  <TableCell className="text-xs">{u.profile_phone ?? u.phone ?? "—"}</TableCell>
                  <TableCell className="text-xs">{(u.providers ?? []).join(", ") || "—"}</TableCell>
                  <TableCell className="text-xs">{(u.roles ?? []).join(", ") || "user"}</TableCell>
                  <TableCell className="text-xs">{u.confirmed ? "Yes" : "No"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {u.last_sign_in_at ? new Date(u.last_sign_in_at).toLocaleString() : "Never"}
                  </TableCell>
                </TableRow>
              ))}
              {filtered.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center text-sm text-muted-foreground">No users match these filters.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function EnquiriesTab() {
  const fn = useServerFn(adminListEnquiries);
  const { data = [], isLoading } = useQuery({ queryKey: ["adminEnquiries"], queryFn: () => fn() });
  if (isLoading) return <p className="p-4">Loading…</p>;
  return (
    <Card className="mt-4">
      <CardHeader><CardTitle>Enquiries ({data.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Name</TableHead><TableHead>Mobile</TableHead>
            <TableHead>Email</TableHead><TableHead>Program</TableHead><TableHead>Event Date</TableHead>
            <TableHead>Location</TableHead><TableHead>I Am</TableHead><TableHead>Message</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.map((e: any) => (
              <TableRow key={e.id}>
                <TableCell className="whitespace-nowrap text-xs">{new Date(e.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{e.name}</TableCell>
                <TableCell>{e.mobile}</TableCell>
                <TableCell className="text-xs">{e.email}</TableCell>
                <TableCell>{e.program_type}</TableCell>
                <TableCell className="text-xs">{e.event_date ?? "-"}</TableCell>
                <TableCell className="text-xs">{e.area}, {e.city}, {e.district}, {e.state} - {e.pincode}</TableCell>
                <TableCell>{e.i_am}</TableCell>
                <TableCell className="max-w-xs text-xs">{e.message ?? "-"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function BookingsTab() {
  const list = useServerFn(adminListBookings);
  const updateStatus = useServerFn(adminUpdateBookingStatus);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["adminBookings"], queryFn: () => list() });
  const m = useMutation({
    mutationFn: (v: { id: string; status: "pending" | "confirmed" | "cancelled" }) => updateStatus({ data: v }),
    onSuccess: () => { toast.success("Updated"); qc.invalidateQueries({ queryKey: ["adminBookings"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  if (isLoading) return <p className="p-4">Loading…</p>;
  return (
    <Card className="mt-4">
      <CardHeader><CardTitle>Bookings ({data.length})</CardTitle></CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Event</TableHead><TableHead>Event Date</TableHead>
            <TableHead>Location</TableHead><TableHead>Phone</TableHead><TableHead>Amount</TableHead>
            <TableHead>UPI Txn</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {data.map((b: any) => (
              <TableRow key={b.id}>
                <TableCell className="text-xs whitespace-nowrap">{new Date(b.created_at).toLocaleDateString()}</TableCell>
                <TableCell>{b.event_name}</TableCell>
                <TableCell className="text-xs">{b.event_date}</TableCell>
                <TableCell className="text-xs">{b.location}</TableCell>
                <TableCell>{b.contact_phone}</TableCell>
                <TableCell>₹{b.amount}</TableCell>
                <TableCell className="text-xs">{b.upi_txn_id ?? "-"}</TableCell>
                <TableCell>
                  <Select value={b.status} onValueChange={(v) => m.mutate({ id: b.id, status: v as any })}>
                    <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="confirmed">Confirmed</SelectItem>
                      <SelectItem value="cancelled">Cancelled</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function EventsTab() {
  const list = useServerFn(adminListEvents);
  const create = useServerFn(adminCreateEvent);
  const del = useServerFn(adminDeleteEvent);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["adminEvents"], queryFn: () => list() });
  const [form, setForm] = useState({ title: "", event_date: "", event_time: "", location: "", poster_url: "", description: "" });
  const cm = useMutation({
    mutationFn: () => create({ data: form }),
    onSuccess: () => { toast.success("Event added"); setForm({ title: "", event_date: "", event_time: "", location: "", poster_url: "", description: "" }); qc.invalidateQueries({ queryKey: ["adminEvents"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const dm = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["adminEvents"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>Add Event</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div><Label>Title</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} /></div>
          <div className="grid grid-cols-2 gap-2">
            <div><Label>Date</Label><Input type="date" value={form.event_date} onChange={(e) => setForm({ ...form, event_date: e.target.value })} /></div>
            <div><Label>Time</Label><Input placeholder="7:00 PM" value={form.event_time} onChange={(e) => setForm({ ...form, event_time: e.target.value })} /></div>
          </div>
          <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
          <div>
            <Label>Poster</Label>
            <Input placeholder="https://… or upload below" value={form.poster_url} onChange={(e) => setForm({ ...form, poster_url: e.target.value })} />
            <Input type="file" accept="image/*" className="mt-2" onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              try { const url = await uploadToGallery(file); setForm((f) => ({ ...f, poster_url: url })); toast.success("Uploaded"); }
              catch (err: any) { toast.error(err.message); }
            }} />
            {form.poster_url && <img src={form.poster_url} alt="" className="mt-2 h-24 rounded object-cover" />}
          </div>
          <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} /></div>
          <Button onClick={() => cm.mutate()} disabled={cm.isPending}>Add Event</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Events ({data.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {isLoading ? <p>Loading…</p> : data.map((ev: any) => (
            <div key={ev.id} className="flex items-center justify-between rounded border p-2 text-sm">
              <div>
                <p className="font-medium">{ev.title}</p>
                <p className="text-xs text-muted-foreground">{ev.event_date} {ev.event_time && `· ${ev.event_time}`} · {ev.location}</p>
              </div>
              <Button size="sm" variant="destructive" onClick={() => dm.mutate(ev.id)}>Delete</Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function GalleryTab() {
  const list = useServerFn(adminListGallery);
  const create = useServerFn(adminCreateGallery);
  const del = useServerFn(adminDeleteGallery);
  const getSize = useServerFn(adminGetGalleryPageSize);
  const setSize = useServerFn(adminSetGalleryPageSize);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["adminGallery"], queryFn: () => list() });
  const { data: pageSize = 6 } = useQuery({ queryKey: ["adminGalleryPageSize"], queryFn: () => getSize() });
  const [sizeInput, setSizeInput] = useState<number>(6);
  useEffect(() => { setSizeInput(pageSize); }, [pageSize]);
  const [form, setForm] = useState({ image_url: "", thumbnail_url: "", caption: "", sort_order: 0 });
  const cm = useMutation({
    mutationFn: () => create({ data: form }),
    onSuccess: () => { toast.success("Image added"); setForm({ image_url: "", thumbnail_url: "", caption: "", sort_order: 0 }); qc.invalidateQueries({ queryKey: ["adminGallery"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const dm = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["adminGallery"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const sm = useMutation({
    mutationFn: (size: number) => setSize({ data: { size } }),
    onSuccess: () => { toast.success("Display size saved"); qc.invalidateQueries({ queryKey: ["adminGalleryPageSize"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const previewItems = data.slice(0, sizeInput);
  return (
    <div className="mt-4 space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Display Settings</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[160px]">
            <Label>Photos shown per page (public gallery)</Label>
            <Input
              type="number" min={1} max={48}
              value={sizeInput}
              onChange={(e) => setSizeInput(Math.min(48, Math.max(1, Number(e.target.value) || 1)))}
            />
          </div>
          <Button onClick={() => sm.mutate(sizeInput)} disabled={sm.isPending || sizeInput === pageSize}>
            Save
          </Button>
          <p className="w-full text-xs text-muted-foreground">
            Currently showing <strong>{pageSize}</strong> per page. Public gallery displays each photo as a 1:1 square — set a custom thumbnail crop below to control exactly what visitors see.
          </p>
        </CardContent>
      </Card>
      <SiteLogoCard />

      <div className="grid gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader><CardTitle>Add Image</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div>
              <Label>Image</Label>
              <Input placeholder="https://… or upload below" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value, thumbnail_url: "" })} />
              <Input type="file" accept="image/*" className="mt-2" onChange={async (e) => {
                const file = e.target.files?.[0]; if (!file) return;
                try { const url = await uploadToGallery(file); setForm((f) => ({ ...f, image_url: url, thumbnail_url: "" })); toast.success("Uploaded"); }
                catch (err: any) { toast.error(err.message); }
              }} />
            </div>
            <div><Label>Caption</Label><Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} /></div>
            <div><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></div>
            {form.image_url && (
              <Suspense fallback={<p className="text-xs text-muted-foreground">Loading cropper…</p>}>
                <ThumbnailCropper
                  imageUrl={form.image_url}
                  hasThumbnail={!!form.thumbnail_url}
                  onClear={() => setForm((f) => ({ ...f, thumbnail_url: "" }))}
                  onConfirm={async (blob: Blob) => {
                    try {
                      const file = new File([blob], `thumb-${Date.now()}.jpg`, { type: "image/jpeg" });
                      const url = await uploadToGallery(file);
                      setForm((f) => ({ ...f, thumbnail_url: url }));
                      toast.success("Thumbnail saved");
                    } catch (err: any) { toast.error(err.message); }
                  }}
                />
              </Suspense>
            )}
            {form.image_url && (
              <div className="rounded-lg border bg-muted/30 p-3">
                <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Front-end preview (1:1) {form.thumbnail_url ? "— custom crop" : "— auto from full image"}
                </p>
                <div className="aspect-square w-32 overflow-hidden rounded-xl bg-card">
                  <img src={form.thumbnail_url || form.image_url} alt="" className="h-full w-full object-cover" />
                </div>
              </div>
            )}
            <Button onClick={() => cm.mutate()} disabled={cm.isPending || !form.image_url}>Add Image</Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle>Gallery ({data.length})</CardTitle></CardHeader>
          <CardContent className="grid grid-cols-2 gap-2">
            {isLoading ? <p>Loading…</p> : data.map((g: any) => (
              <div key={g.id} className="relative overflow-hidden rounded border">
                <div className="aspect-square w-full overflow-hidden bg-card">
                  <img src={g.thumbnail_url || g.image_url} alt={g.caption ?? ""} className="h-full w-full object-cover" />
                </div>
                <div className="p-2 text-xs">
                  <p className="truncate">{g.caption ?? "—"}</p>
                  {g.thumbnail_url && <p className="text-[10px] text-muted-foreground">Custom crop</p>}
                  <Button size="sm" variant="destructive" className="mt-1 w-full" onClick={() => dm.mutate(g.id)}>Delete</Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
      <Card>
        <CardHeader>
          <CardTitle>Public Preview — first {sizeInput} photo{sizeInput === 1 ? "" : "s"}</CardTitle>
        </CardHeader>
        <CardContent>
          {previewItems.length === 0 ? (
            <p className="text-sm text-muted-foreground">No images yet.</p>
          ) : (
            <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {previewItems.map((g: any) => (
                <div key={g.id} className="aspect-square overflow-hidden rounded-xl bg-card">
                  <img src={g.thumbnail_url || g.image_url} alt={g.caption ?? ""} className="h-full w-full object-cover" />
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

function SiteLogoCard() {
  const getLogo = useServerFn(adminGetSiteLogo);
  const setLogo = useServerFn(adminSetSiteLogo);
  const qc = useQueryClient();
  const { data: current = "" } = useQuery({ queryKey: ["adminSiteLogo"], queryFn: () => getLogo() });
  const [url, setUrl] = useState("");
  useEffect(() => { setUrl(current || ""); }, [current]);
  const m = useMutation({
    mutationFn: (v: string) => setLogo({ data: { url: v } }),
    onSuccess: () => {
      toast.success("Logo updated");
      qc.invalidateQueries({ queryKey: ["adminSiteLogo"] });
      qc.invalidateQueries({ queryKey: ["siteLogo"] });
    },
    onError: (e: any) => toast.error(e?.message || "Failed to save"),
  });
  return (
    <Card>
      <CardHeader><CardTitle>Site Logo (Header)</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        <div className="flex items-center gap-4">
          <div className="h-16 w-16 overflow-hidden rounded-full ring-2 ring-gold/60 bg-card">
            <img src={url || "/site-logo.webp"} alt="Logo preview" className="h-full w-full object-cover" />
          </div>
          <div className="flex-1 space-y-2">
            <Label>Logo URL</Label>
            <Input placeholder="https://… or upload below" value={url} onChange={(e) => setUrl(e.target.value)} />
            <Input type="file" accept="image/*" onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              try { const u = await uploadToGallery(file); setUrl(u); toast.success("Uploaded"); }
              catch (err: any) { toast.error(err.message); }
            }} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => m.mutate(url)} disabled={m.isPending || url === (current || "")}>Save Logo</Button>
          {current && (
            <Button variant="outline" onClick={() => m.mutate("")} disabled={m.isPending}>Reset to Default</Button>
          )}
        </div>
        <p className="text-xs text-muted-foreground">The header text "Surer Adda with Sanchayeeta" stays fixed. Only the circular logo image changes.</p>
      </CardContent>
    </Card>
  );
}

function AdsTab() {
  const list = useServerFn(adminListAds);
  const create = useServerFn(adminCreateAd);
  const del = useServerFn(adminDeleteAd);
  const toggle = useServerFn(adminToggleAd);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["adminAds"], queryFn: () => list() });
  const [form, setForm] = useState({ image_url: "", thumbnail_url: "", caption: "", link_url: "", active: true, sort_order: 0 });
  const cm = useMutation({
    mutationFn: () => create({ data: { ...form, sort_order: Number.isFinite(form.sort_order) ? form.sort_order : 0 } }),
    onSuccess: () => { toast.success("Banner added"); setForm({ image_url: "", thumbnail_url: "", caption: "", link_url: "", active: true, sort_order: 0 }); qc.invalidateQueries({ queryKey: ["adminAds"] }); },
    onError: (e: any) => { console.error("adminCreateAd failed:", e); toast.error(e?.message || "Failed to add banner"); },
  });
  const dm = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["adminAds"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const tm = useMutation({
    mutationFn: (v: { id: string; active: boolean }) => toggle({ data: v }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["adminAds"] }),
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>Add Banner</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Banner Image</Label>
            <Input placeholder="https://… or upload below" value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value, thumbnail_url: "" })} />
            <Input type="file" accept="image/*" className="mt-2" onChange={async (e) => {
              const file = e.target.files?.[0]; if (!file) return;
              try { const url = await uploadToGallery(file); setForm((f) => ({ ...f, image_url: url, thumbnail_url: "" })); toast.success("Uploaded"); }
              catch (err: any) { toast.error(err.message); }
            }} />
          </div>
          <div><Label>Caption (optional)</Label><Input value={form.caption} onChange={(e) => setForm({ ...form, caption: e.target.value })} /></div>
          <div><Label>Link URL (optional)</Label><Input placeholder="https://…" value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} /></div>
          <div className="flex items-center gap-4">
            <div className="flex-1"><Label>Sort Order</Label><Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} /></div>
            <div className="flex items-center gap-2 pt-6"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} /><Label>Active</Label></div>
          </div>
          {form.image_url && (
            <Suspense fallback={<p className="text-xs text-muted-foreground">Loading cropper…</p>}>
              <ThumbnailCropper
                imageUrl={form.image_url}
                hasThumbnail={!!form.thumbnail_url}
                onClear={() => setForm((f) => ({ ...f, thumbnail_url: "" }))}
                onConfirm={async (blob: Blob) => {
                  try {
                    const file = new File([blob], `thumb-${Date.now()}.jpg`, { type: "image/jpeg" });
                    const url = await uploadToGallery(file);
                    setForm((f) => ({ ...f, thumbnail_url: url }));
                    toast.success("Thumbnail saved");
                  } catch (err: any) { toast.error(err.message); }
                }}
              />
            </Suspense>
          )}
          {form.image_url && (
            <div className="space-y-3 rounded-lg border bg-muted/30 p-3">
              <p className="text-xs font-medium text-muted-foreground">Live Preview</p>
              <div>
                <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">Full Banner</p>
                <div className="overflow-hidden rounded-lg border">
                  <img src={form.image_url} alt={form.caption ?? ""} className="h-48 w-full object-cover" />
                  {form.caption && <p className="bg-background p-2 text-sm">{form.caption}</p>}
                </div>
              </div>
              <div>
                <p className="mb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                  Thumbnail {form.thumbnail_url ? "(cropped)" : "(auto from banner)"}
                </p>
                <div className="flex gap-2">
                  <img src={form.thumbnail_url || form.image_url} alt="" className="h-16 w-16 rounded object-cover border" />
                  <img src={form.thumbnail_url || form.image_url} alt="" className="h-24 w-24 rounded object-cover border" />
                </div>
              </div>
            </div>
          )}
          <Button onClick={() => cm.mutate()} disabled={cm.isPending || !form.image_url}>Add Banner</Button>
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Banners ({data.length})</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          {isLoading ? <p>Loading…</p> : data.map((a: any) => (
            <div key={a.id} className="overflow-hidden rounded border">
              <img src={a.image_url} alt={a.caption ?? ""} className="h-32 w-full object-cover" />
              <div className="space-y-2 p-2 text-xs">
                <p className="truncate font-medium">{a.caption ?? "—"}</p>
                {a.link_url && <p className="truncate text-muted-foreground">{a.link_url}</p>}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2"><Switch checked={a.active} onCheckedChange={(v) => tm.mutate({ id: a.id, active: v })} /><span>{a.active ? "Active" : "Hidden"}</span></div>
                  <Button size="sm" variant="destructive" onClick={() => dm.mutate(a.id)}>Delete</Button>
                </div>
              </div>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

function FbPostsTab() {
  const list = useServerFn(adminListFbPosts);
  const create = useServerFn(adminCreateFbPost);
  const del = useServerFn(adminDeleteFbPost);
  const qc = useQueryClient();
  const { data = [], isLoading } = useQuery({ queryKey: ["adminFbPosts"], queryFn: () => list() });
  const [form, setForm] = useState({ post_url: "", is_video: false, sort_order: 0 });
  const cm = useMutation({
    mutationFn: () => create({ data: { ...form, sort_order: Number.isFinite(form.sort_order) ? form.sort_order : 0 } }),
    onSuccess: () => { toast.success("Facebook post added"); setForm({ post_url: "", is_video: false, sort_order: 0 }); qc.invalidateQueries({ queryKey: ["adminFbPosts"] }); },
    onError: (e: any) => toast.error(e?.message || "Failed to add"),
  });
  const dm = useMutation({
    mutationFn: (id: string) => del({ data: { id } }),
    onSuccess: () => { toast.success("Deleted"); qc.invalidateQueries({ queryKey: ["adminFbPosts"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  return (
    <div className="mt-4 grid gap-4 lg:grid-cols-2">
      <Card>
        <CardHeader><CardTitle>Add Facebook Post / Video</CardTitle></CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Facebook Post URL</Label>
            <Input
              placeholder="https://www.facebook.com/.../posts/... or /videos/..."
              value={form.post_url}
              onChange={(e) => setForm({ ...form, post_url: e.target.value })}
            />
            <p className="mt-1 text-xs text-muted-foreground">
              Open your post on Facebook, copy the URL from the address bar, and paste here.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.is_video} onCheckedChange={(v) => setForm({ ...form, is_video: v })} />
            <Label>This is a video (enables autoplay)</Label>
          </div>
          <div>
            <Label>Sort Order (lower shows first)</Label>
            <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
          </div>
          <Button onClick={() => cm.mutate()} disabled={cm.isPending || !form.post_url}>Add Post</Button>
          <p className="text-xs text-muted-foreground">
            Only the 3 newest (by sort order, then date) appear on the homepage. To replace an old post, delete it below and add the new one.
          </p>
          {form.post_url && (
            <div className="mt-4 border-t pt-4">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Preview</p>
              <Suspense fallback={<p className="text-xs text-muted-foreground">Loading preview…</p>}>
                <FacebookFeed
                  key={`${form.post_url}-${form.is_video}`}
                  posts={[{ id: "preview", post_url: form.post_url, is_video: form.is_video }]}
                />
              </Suspense>
            </div>
          )}
        </CardContent>
      </Card>
      <Card>
        <CardHeader><CardTitle>Posts ({data.length})</CardTitle></CardHeader>
        <CardContent className="space-y-2">
          {isLoading ? <p>Loading…</p> : data.map((p: any, i: number) => (
            <div key={p.id} className="flex items-center justify-between gap-3 rounded border p-2 text-xs">
              <div className="min-w-0 flex-1">
                <p className="font-medium">{i < 3 ? `#${i + 1} (live)` : `#${i + 1}`} · {p.is_video ? "Video" : "Post"} · sort {p.sort_order}</p>
                <a href={p.post_url} target="_blank" rel="noopener noreferrer" className="block truncate text-muted-foreground hover:underline">{p.post_url}</a>
              </div>
              <Button size="sm" variant="destructive" onClick={() => dm.mutate(p.id)}>Delete</Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}

// =========================================================
// Admin Users tab
// =========================================================
const PERM_LABELS: Record<string, string> = {
  analytics: "Analytics",
  users: "Users",
  enquiries: "Enquiries",
  bookings: "Bookings",
  events: "Events",
  gallery: "Gallery",
  ads: "Advertisements",
  fb: "Facebook Posts",
  admins: "Admin Users",
};

function PermissionPicker({ value, onChange }: { value: string[]; onChange: (next: string[]) => void }) {
  const fullAccess = value.includes("*");
  const set = (perm: string, checked: boolean) => {
    if (perm === "*") {
      onChange(checked ? ["*"] : []);
      return;
    }
    const without = value.filter((v) => v !== "*" && v !== perm);
    onChange(checked ? [...without, perm] : without);
  };
  return (
    <div className="space-y-2">
      <label className="flex items-center gap-2 rounded-md border p-2 text-sm">
        <Checkbox checked={fullAccess} onCheckedChange={(c) => set("*", !!c)} />
        <span className="font-medium">Full access (all tabs)</span>
      </label>
      <div className={cn("grid grid-cols-2 gap-2", fullAccess && "opacity-50 pointer-events-none")}>
        {PERMISSION_KEYS.map((p) => (
          <label key={p} className="flex items-center gap-2 rounded-md border p-2 text-sm">
            <Checkbox checked={fullAccess || value.includes(p)} onCheckedChange={(c) => set(p, !!c)} disabled={fullAccess} />
            <span>{PERM_LABELS[p]}</span>
          </label>
        ))}
      </div>
    </div>
  );
}

function AdminsTab() {
  const list = useServerFn(adminListAdmins);
  const invite = useServerFn(adminInviteAdmin);
  const remove = useServerFn(adminRemoveAdmin);
  const updatePerms = useServerFn(adminUpdateAdminPermissions);
  const revoke = useServerFn(adminRevokeInvitation);
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["adminAdmins"], queryFn: () => list() });

  const [inviteOpen, setInviteOpen] = useState(false);
  const [inviteEmail, setInviteEmail] = useState("");
  const [invitePerms, setInvitePerms] = useState<string[]>(["*"]);

  const inviteM = useMutation({
    mutationFn: () => invite({ data: { email: inviteEmail, permissions: invitePerms as PermissionKey[] } }),
    onSuccess: (r: any) => {
      if (r.mode === "granted_existing") toast.success("That user already exists — admin role granted.");
      else toast.success(r.emailed ? "Invitation sent." : `Invitation saved but email not delivered (${r.reason ?? "unknown"}).`);
      setInviteOpen(false); setInviteEmail(""); setInvitePerms(["*"]);
      qc.invalidateQueries({ queryKey: ["adminAdmins"] });
      qc.invalidateQueries({ queryKey: ["adminMe"] });
    },
    onError: (e: any) => toast.error(e.message),
  });
  const removeM = useMutation({
    mutationFn: (user_id: string) => remove({ data: { user_id } }),
    onSuccess: () => { toast.success("Admin removed"); qc.invalidateQueries({ queryKey: ["adminAdmins"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const revokeM = useMutation({
    mutationFn: (id: string) => revoke({ data: { id } }),
    onSuccess: () => { toast.success("Invitation revoked"); qc.invalidateQueries({ queryKey: ["adminAdmins"] }); },
    onError: (e: any) => toast.error(e.message),
  });
  const permsM = useMutation({
    mutationFn: (v: { user_id: string; permissions: string[] }) =>
      updatePerms({ data: { user_id: v.user_id, permissions: v.permissions as PermissionKey[] } }),
    onSuccess: () => { toast.success("Permissions updated"); qc.invalidateQueries({ queryKey: ["adminAdmins"] }); qc.invalidateQueries({ queryKey: ["adminMe"] }); },
    onError: (e: any) => toast.error(e.message),
  });

  if (isLoading) return <p className="p-4">Loading…</p>;
  const admins = data?.admins ?? [];
  const pending = data?.pendingInvites ?? [];

  return (
    <div className="mt-4 space-y-4">
      <div className="flex items-center justify-between gap-2">
        <h2 className="text-lg font-semibold">Admin users</h2>
        <Dialog open={inviteOpen} onOpenChange={setInviteOpen}>
          <DialogTrigger asChild><Button size="sm"><Mail className="mr-1 h-4 w-4" />Invite admin</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Invite a new admin</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Email</Label>
                <Input type="email" placeholder="name@example.com" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} />
              </div>
              <div>
                <Label className="mb-2 block">Tab permissions</Label>
                <PermissionPicker value={invitePerms} onChange={setInvitePerms} />
              </div>
              <p className="text-xs text-muted-foreground">
                If this person hasn't signed up yet, they'll receive an email with a signup link.
                Admin access is granted automatically the moment they create their account.
              </p>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setInviteOpen(false)}>Cancel</Button>
              <Button onClick={() => inviteM.mutate()} disabled={!inviteEmail || invitePerms.length === 0 || inviteM.isPending}>
                {inviteM.isPending ? "Sending…" : "Send invite"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardHeader><CardTitle>Current admins ({admins.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Email</TableHead><TableHead>Name</TableHead>
              <TableHead>Access</TableHead><TableHead>Last sign-in</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {admins.map((a: any) => (
                <TableRow key={a.user_id}>
                  <TableCell className="text-xs">
                    {a.email ?? "—"} {a.is_self && <Badge variant="secondary" className="ml-1">you</Badge>}
                  </TableCell>
                  <TableCell className="text-xs">{a.full_name ?? "—"}</TableCell>
                  <TableCell>
                    <Popover>
                      <PopoverTrigger asChild>
                        <Button variant="outline" size="sm" className="h-7 text-xs">{a.permissionsLabel}</Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-80 p-3" align="start">
                        <EditPermissions
                          initial={a.permissions}
                          onSave={(p) => permsM.mutate({ user_id: a.user_id, permissions: p })}
                          busy={permsM.isPending}
                        />
                      </PopoverContent>
                    </Popover>
                  </TableCell>
                  <TableCell className="whitespace-nowrap text-xs">
                    {a.last_sign_in_at ? new Date(a.last_sign_in_at).toLocaleString() : "Never"}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" disabled={a.is_self || removeM.isPending}
                      onClick={() => { if (confirm(`Remove ${a.email} as admin?`)) removeM.mutate(a.user_id); }}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {admins.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No admins yet.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card>
        <CardHeader><CardTitle>Pending invitations ({pending.length})</CardTitle></CardHeader>
        <CardContent className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Email</TableHead><TableHead>Access</TableHead>
              <TableHead>Sent</TableHead><TableHead>Expires</TableHead><TableHead></TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {pending.map((p: any) => (
                <TableRow key={p.id}>
                  <TableCell className="text-xs">{p.email}</TableCell>
                  <TableCell className="text-xs">{p.permissionsLabel}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(p.created_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-xs whitespace-nowrap">{new Date(p.expires_at).toLocaleDateString()}</TableCell>
                  <TableCell className="text-right">
                    <Button variant="ghost" size="icon" disabled={revokeM.isPending}
                      onClick={() => { if (confirm(`Revoke invitation for ${p.email}?`)) revokeM.mutate(p.id); }}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {pending.length === 0 && (
                <TableRow><TableCell colSpan={5} className="text-center text-sm text-muted-foreground">No pending invitations.</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
}

function EditPermissions({ initial, onSave, busy }: { initial: string[]; onSave: (p: string[]) => void; busy: boolean }) {
  const [perms, setPerms] = useState<string[]>(initial);
  return (
    <div className="space-y-3">
      <PermissionPicker value={perms} onChange={setPerms} />
      <Button size="sm" className="w-full" onClick={() => onSave(perms)} disabled={busy || perms.length === 0}>
        {busy ? "Saving…" : "Save permissions"}
      </Button>
    </div>
  );
}

